import { apiClient, APIResponse } from './client';
import { WorkgroupMembership } from './auth';

// The access token is intentionally absent -- it stays server-side.
export interface GithubConnectResult {
  connected: boolean;
  repoprovider?: string;
  repousername?: string;
  repoid?: string;
}

export interface Profile {
  id: number;
  userid: number;
  code: string;
  firstname: string;
  lastname?: string;
  emailid: string;
  mobile?: string;
  alternateemailid?: string;
  alternatemobile?: string;
  address?: string;
  country?: string;
  city?: string;
  statename?: string;
  pincode?: string;
  dob?: string;
  department?: string;
  designation?: string;
  registeredon: string;
  role?: string;
  profiletype?: string;
  reportingmanagerid?: number;
  repoid?: string;
  repousername?: string;
  // The ciphertext is never returned by the API -- use this flag for "GitHub connected"
  // checks instead. connectGithubOAuth's response (GithubConnectResult, above) never
  // carries the token either -- it stays server-side and is copied profile -> project
  // via ProjectAPI.updateRepoFromProfile.
  hasrepoaccesstoken?: boolean;
  repoprovider?: string;
  demographics?: string;
  status: string;
  isarchived?: boolean;
  isactive?: boolean;
  isdeleted?: boolean;
  createdon?: string;
  createdby?: number;
  lastactivityon?: string;
  lastactivityby?: number;
  avatar?: string;
  timezone?: string;
  workgroups?: WorkgroupMembership[];
}

export interface ClientProfile {
  id: number;
  firstName: string;
  lastName: string;
  emailid: string;
  mobile: string;
  billingAddress?: {
    title: string;
    address: string;
    city: string;
    statename: string;
    pincode: string;
    country: string;
  };
  designation?: string;
  department?: string;
  role?: string;
  avatar?: string;
  timezone?: string;
}

export interface UpdateProfileData {
  firstname?: string;
  lastname?: string;
  emailid?: string;
  mobile?: string;
  designation?: string;
  department?: string;
  role?: string;
  avatar?: string;
  timezone?: string;
  address?: string;
  city?: string;
  statename?: string;
  country?: string;
  pincode?: string;
}

export interface AvatarUploadResponse {
  avatarUrl: string;
  message: string;
}

/** The caller's resolved role-permission grid for the active workgroup (see AccessControlService.getEffectivePermissions on the backend). */
export interface EffectivePermission {
  modulecode: string;
  actions: string[];
}

export interface LoginInfoResponse {
  user: Profile;
  permission: EffectivePermission[];
  /** The caller's resolved plan/entitlement grid for the active workgroup's current package (see EntitlementService.getEffectiveEntitlements on the backend). Same shape as `permission`, ANDed with it by useCanAccess. */
  entitlement: EffectivePermission[];
}

export interface CreateOrganisationData {
  title: string;
  contactpersonname: string;
  emailid: string;

  country?: string;
  address?: string;
  city?: string;
  statename?: string;
  pincode?: string;
  description?: string;
  selectedPackage?: number;
  selectedAddons?: number[];
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export class ProfileAPI  {
  static async getProfile() {
    return apiClient.get<LoginInfoResponse>('/auth/login-info');
  }
  static async updateProfile(id: number, data: UpdateProfileData) {
    return apiClient.patch<Profile>(`/profiles/${id}`, data);
  }

  static async uploadAvatar(profileId: number, file: File): Promise<APIResponse<AvatarUploadResponse>> {
    const formData = new FormData();
    formData.append('avatar', file);
    return apiClient.upload<AvatarUploadResponse>(`/profiles/${profileId}/avatar`, formData);
  }

  static async uploadAvatarAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        resolve(base64String);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }
  static async createOrganisation(data: CreateOrganisationData) {
    return apiClient.post<any>('/profiles/organisation', data);
  }

  static async getOrganisationAddress(workgroupId: number) {
    return apiClient.get<{ title: string; address?: string; city?: string; statename?: string; pincode?: string; country?: string } | null>(`/profiles/organisation/address/${workgroupId}`);
  }

  static async changePassword(data: ChangePasswordData) {
    return apiClient.post<void>('/profiles/change-password', data);
  }
  static async deleteAccount() {
    return apiClient.delete<void>('/profiles/delete-account');
  }

  static async connectGithubOAuth(code: string) {
    return apiClient.post<GithubConnectResult>('/profiles/connect-github', { code });
  }

  static async disconnectGithub() {
    return apiClient.post<void>('/profiles/disconnect-github');
  }
};
